import { ExternalLink, Sparkles } from 'lucide-react';
import { Link } from 'react-router';

import { Button } from '@/components/ui/button';
import type {
  FundTransactionDetail,
  ReconciliationStateResponse,
} from '@/types/fund-transactions';
import { formatCurrency } from '@/utils/formatters';

import { Field } from './transactionDetails';
import { TransactionDetailsCard } from './TransactionDetailsCard';

export function DistributionDetailsCard({
  fundId,
  transaction,
  fundName,
  reconciliation,
  onViewJournalEntries,
}: {
  fundId: string;
  transaction: FundTransactionDetail;
  fundName: string;
  reconciliation: ReconciliationStateResponse | undefined;
  onViewJournalEntries?: () => void;
}) {
  const snapshot = transaction.distribution;

  return (
    <TransactionDetailsCard
      transaction={transaction}
      fundName={fundName}
      reconciliation={reconciliation}
      onViewJournalEntries={onViewJournalEntries}
      icon={<Sparkles className="h-5 w-5" />}
      iconClassName="bg-emerald-100 text-emerald-600 dark:bg-emerald-950/40 dark:text-emerald-300"
      eyebrow={
        snapshot
          ? `Distribution #${snapshot.distribution_number}`
          : 'Distribution'
      }
      actions={
        snapshot && (
          <Button asChild variant="outline" size="sm">
            <Link
              to={`/app/funds/${fundId}/distributions/${snapshot.distribution_id}`}
              data-testid="view-distribution-link"
            >
              <ExternalLink className="h-4 w-4 mr-2" />
              View distribution
            </Link>
          </Button>
        )
      }
      fields={
        snapshot && (
          <>
            <Field label="Gross amount">
              {formatCurrency(snapshot.gross_amount, {
                currency: transaction.currency,
              })}
            </Field>
            <Field label="Deductions">
              {formatCurrency(snapshot.total_deductions, {
                currency: transaction.currency,
              })}
            </Field>
            <Field label="Net amount">
              {formatCurrency(snapshot.net_amount, {
                currency: transaction.currency,
              })}
            </Field>
          </>
        )
      }
    />
  );
}
